import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdAutoAwesome,
    MdBusiness,
    MdCheckCircle,
    MdCloudUpload,
    MdDescription,
    MdDocumentScanner,
    MdEdit,
    MdFactCheck,
    MdEditCalendar,
    MdEvent,
    MdFolder,
    MdHourglassTop,
    MdInfoOutline,
    MdInsertDriveFile,
    MdMemory,
    MdOpenInNew,
    MdPauseCircle,
    MdSdStorage,
    MdTag,
    MdClose,
    MdMovie,
    MdHeadphones,
    MdGraphicEq,
    MdDownload,
    MdContentCopy,
    MdCheck,
    MdPlayArrow,
} from 'react-icons/md';
import { FaYoutube, FaPlay } from 'react-icons/fa';
import { LuFileText, LuGlobe as LuGlobe2 } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../../Utils/Permissions';
import { getAegisAiDocumentById, getAegisAiYoutubePreview, getAegisAiDocumentMedia } from '../../../Services/AegisAiDocumentsService';
import { APPRoutes } from '../../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../../Utils/DateTime';
import ViewEmptyState from '../../../Components/Common/ViewEmptyState';
import FileActions from '../../../Components/Common/FileActions';
import AiVideoPlayerModal from '../../../Components/Common/AiVideoPlayerModal';
import AiAudioPlayerModal from '../../../Components/Common/AiAudioPlayerModal';
import { downloadProviderFile } from '../../../Services/ProviderService';
import { AiCard, AiCardSkeleton, AiStatusPill, AiViewSkeleton, formatFileSize, getAiSourceType, getFileMeta, inferAiSourceType } from '../AegisAiUi';
import BackButton from '../../../Components/Common/BackButton';
import AiTextEditor from './AiTextEditor';
import { MdOutlineDescription } from 'react-icons/md';

const STATUS_STYLE = {
    Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)', icon: MdCheckCircle, label: 'Active', tone: 'success' },
    Pending: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)', icon: MdPauseCircle, label: 'Pending', tone: 'pending' },
    Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)', icon: MdPauseCircle, label: 'Inactive', tone: 'inactive' },
    Unknown: { backgroundColor: 'var(--hc-status-unknown-bg)', color: 'var(--hc-status-unknown-text)', icon: MdPauseCircle, label: 'Unknown', tone: 'inactive' },
};

const getStatusMeta = (statusValue) => {
    const norm = (statusValue || '').toLowerCase().trim();
    if (norm === 'active') return STATUS_STYLE.Active;
    if (norm === 'pending' || norm === 'not_indexed') return STATUS_STYLE.Pending;
    if (norm === 'inactive') return STATUS_STYLE.Inactive;
    return { ...STATUS_STYLE.Unknown, label: statusValue || 'Unknown' };
};

// FileName is stored with its blob folder (e.g. "AegisAI/<client>/Documents/report.pdf").
const getBaseName = (path) => String(path || '').split(/[\\/]/).filter(Boolean).pop() || '';

const FILE_TYPE_LABELS = {
    PDF: 'PDF document',
    DOC: 'Word document (97-2003)',
    DOCX: 'Word document',
    TXT: 'Plain text',
    MD: 'Markdown',
    MP3: 'MP3 audio',
    WAV: 'WAV audio',
    M4A: 'M4A audio',
    AAC: 'AAC audio',
    OGG: 'OGG audio',
    FLAC: 'FLAC audio',
    MP4: 'MP4 video',
    MOV: 'QuickTime video',
    WEBM: 'WebM video',
    MKV: 'Matroska video',
    AVI: 'AVI video',
};

// The preview endpoint only returns PDFs; the download endpoint returns any file type as-is,
// and FileActions picks the right renderer (PDF, DOCX, audio, video...) from it.
const previewAnyFile = (token, fileId) => downloadProviderFile(token, fileId);

// Visual state of the Upload → Processing → Indexed pipeline for each status.
const PIPELINE_STEPS = [
    { key: 'upload', label: 'Uploaded', caption: 'File received', icon: MdCloudUpload },
    { key: 'process', label: 'Processing', caption: 'Chunk & embed', icon: MdMemory },
    { key: 'index', label: 'Indexed', caption: 'Ready for answers', icon: MdCheckCircle },
];

const getPipelineState = (tone) => {
    if (tone === 'success') {
        return {
            steps: ['done', 'done', 'done'],
            callout: { tone: 'success', icon: MdCheckCircle, text: "Indexing is complete. This document is available to the client's RAG assistant." },
        };
    }
    if (tone === 'pending') {
        return {
            steps: ['done', 'active', 'todo'],
            callout: { tone: 'pending', icon: MdHourglassTop, text: 'RAG indexing is running in the background. The status will change to Active once vector indexing is complete.' },
        };
    }
    return {
        steps: ['done', 'done', 'muted'],
        callout: { tone: 'muted', icon: MdPauseCircle, text: 'This document is currently not active.' },
    };
};

const getYoutubeVideoId = (url) => {
    const match = String(url || '').match(/(?:youtu\.be\/|[?&]v=|\/(?:shorts|live|embed)\/)([a-zA-Z0-9_-]{11})/i);
    return match ? match[1] : null;
};

function AiYouTubePreview({ url, title, token }) {
    const videoId = React.useMemo(() => getYoutubeVideoId(url), [url]);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [thumbIndex, setThumbIndex] = React.useState(0);
    const [copied, setCopied] = React.useState(false);
    const [meta, setMeta] = React.useState(null);

    React.useEffect(() => {
        if (!url || !token) return;
        let cancelled = false;
        getAegisAiYoutubePreview(url, token)
            .then((res) => {
                if (!cancelled && res?.is_success && res?.data) {
                    setMeta(res.data);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [url, token]);

    const thumbCandidates = React.useMemo(() => {
        if (!videoId) return [];
        return [
            `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        ];
    }, [videoId]);

    const currentThumb = thumbCandidates[thumbIndex] || thumbCandidates[thumbCandidates.length - 1];

    const handleThumbError = () => {
        if (thumbIndex < thumbCandidates.length - 1) {
            setThumbIndex((prev) => prev + 1);
        }
    };

    const handleCopy = () => {
        if (!url) return;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    };

    if (!videoId) {
        return (
            <div className="hc-ai-file d-flex align-items-center p-3 rounded-4">
                <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': '#e52d27' }}>
                    <FaYoutube size={26} />
                </span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="hc-ai-file-name fw-bold text-truncate">YouTube link</div>
                    <div className="hc-ai-file-meta text-truncate" title={url}>{url}</div>
                </div>
                <a href={url} target="_blank" rel="noopener noreferrer" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline flex-shrink-0">
                    <MdOpenInNew size={16} />
                    <span>Open</span>
                </a>
            </div>
        );
    }

    const videoTitle = meta?.Title || title || 'YouTube Video';
    const channelName = meta?.AuthorName;

    return (
        <div className="hc-ai-youtube-preview">
            <div className="hc-ai-youtube-frame position-relative">
                {isPlaying ? (
                    <div className="w-100 h-100 position-relative">
                        <iframe
                            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
                            title={videoTitle}
                            className="w-100 h-100 border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                        <button
                            type="button"
                            className="hc-ai-youtube-close-btn position-absolute top-0 end-0 m-3 d-inline-flex align-items-center gap-1"
                            onClick={() => setIsPlaying(false)}
                            title="Return to preview thumbnail"
                        >
                            <MdClose size={15} />
                            <span>Close Player</span>
                        </button>
                    </div>
                ) : (
                    <div
                        className="hc-ai-youtube-thumb-wrapper"
                        onClick={() => setIsPlaying(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsPlaying(true); }}
                        aria-label={`Play YouTube video: ${videoTitle}`}
                    >
                        <img
                            src={currentThumb}
                            alt={videoTitle}
                            className="hc-ai-youtube-thumb"
                            onError={handleThumbError}
                        />
                        <div className="hc-ai-youtube-overlay">
                            <div className="hc-ai-youtube-badge-top">
                                <span className="hc-ai-youtube-pill">
                                    <FaYoutube size={15} color="#ff0000" />
                                    <span>YouTube</span>
                                </span>
                                <span className="hc-ai-youtube-pill" style={{ opacity: 0.92, fontSize: '11.5px' }}>
                                    <MdPlayArrow size={15} />
                                    <span>Click to Play</span>
                                </span>
                            </div>

                            <div className="hc-ai-youtube-play-btn-center">
                                <div className="hc-ai-youtube-play-icon">
                                    <FaPlay size={18} style={{ marginLeft: 3 }} />
                                </div>
                                <span className="hc-ai-youtube-play-label">Play Video</span>
                            </div>

                            <div className="hc-ai-youtube-bottom-info">
                                <div className="hc-ai-youtube-title text-truncate" title={videoTitle}>{videoTitle}</div>
                                {channelName ? (
                                    <div className="hc-ai-youtube-author text-truncate" style={{ fontSize: '12px', opacity: 0.85 }}>
                                        {channelName}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="hc-ai-youtube-meta-bar">
                <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                    <FaYoutube size={18} color="#e52d27" className="flex-shrink-0" />
                    <span className="hc-ai-youtube-url-text text-truncate" title={url}>{url}</span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        className="hc-ai-youtube-action-btn hc-ai-youtube-btn-secondary"
                        onClick={handleCopy}
                        title="Copy video link"
                    >
                        {copied ? (
                            <>
                                <MdCheck size={15} color="#10b981" />
                                <span>Copied</span>
                            </>
                        ) : (
                            <>
                                <MdContentCopy size={15} />
                                <span>Copy Link</span>
                            </>
                        )}
                    </button>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hc-ai-youtube-action-btn hc-ai-youtube-btn-yt"
                        title="Watch directly on YouTube"
                    >
                        <FaYoutube size={16} />
                        <span>Watch on YouTube</span>
                        <MdOpenInNew size={14} />
                    </a>
                </div>
            </div>
        </div>
    );
}


function AiBlobAudioPreview({ document, fileMeta, fileSize, fileBaseName, token, onOpenModal, onDownload }) {
    const audioTitle = document?.Title || fileBaseName || 'Audio Recording';
    const ext = (fileMeta?.ext || 'audio').toUpperCase();

    return (
        <div className="hc-ai-blob-audio-preview">
            <div
                className="hc-ai-blob-audio-frame"
                onClick={onOpenModal}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenModal(); }}
                aria-label={`Preview audio: ${audioTitle}`}
            >
                <div className="hc-ai-blob-audio-bg-glow" />

                <div className="hc-ai-blob-audio-overlay">
                    <div className="hc-ai-blob-audio-badge-top">
                        <span className="hc-ai-blob-audio-pill">
                            <MdHeadphones size={15} color="#38bdf8" />
                            <span>Audio Recording</span>
                        </span>
                        <span className="hc-ai-blob-audio-pill" style={{ opacity: 0.95, fontSize: '11.5px' }}>
                            <MdPlayArrow size={15} />
                            <span>Preview in Popup</span>
                        </span>
                    </div>

                    <div className="hc-ai-blob-audio-center">
                        <div className="hc-ai-blob-audio-play-icon">
                            <FaPlay size={20} style={{ marginLeft: 3 }} />
                        </div>
                        <span className="hc-ai-blob-audio-play-label">Preview Audio</span>
                    </div>

                    <div className="hc-ai-blob-audio-bottom-info">
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <MdGraphicEq size={18} color="#38bdf8" />
                            <div className="hc-ai-blob-audio-title text-truncate" title={audioTitle}>{audioTitle}</div>
                        </div>
                        <div className="hc-ai-blob-audio-filename text-truncate">
                            {[fileMeta?.ext ? `${ext} Audio` : 'Audio Recording', fileSize].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                </div>
            </div>

            <div className="hc-ai-blob-video-meta-bar">
                <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                    <MdHeadphones size={20} color="#0ea5e9" className="flex-shrink-0" />
                    <div className="text-truncate">
                        <div className="fw-semibold text-truncate small">{fileBaseName || 'Attached audio file'}</div>
                        <div className="text-muted text-truncate" style={{ fontSize: '11.5px' }}>
                            {[fileMeta?.ext ? `${ext} audio format` : null, fileSize].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        className="hc-ai-blob-video-action-btn hc-ai-blob-audio-btn-play"
                        onClick={onOpenModal}
                    >
                        <MdPlayArrow size={17} />
                        <span>Preview in Popup</span>
                    </button>
                    {onDownload ? (
                        <button
                            type="button"
                            className="hc-ai-blob-video-action-btn hc-ai-blob-video-btn-secondary"
                            onClick={onDownload}
                            title="Download audio"
                        >
                            <MdDownload size={16} />
                            <span>Download</span>
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function AiBlobVideoPreview({ document, fileMeta, fileSize, fileBaseName, token, posterUrl, onOpenModal, onDownload }) {
    const [thumbnailUrl, setThumbnailUrl] = React.useState(posterUrl || null);

    React.useEffect(() => {
        if (!document?.Id || !token) return;
        let cancelled = false;
        const controller = new AbortController();

        if (posterUrl) {
            setThumbnailUrl(posterUrl);
            return;
        }
        if (document.ThumbnailFileId || document.SourceType === 'video' || document.MediaFileId) {
            getAegisAiDocumentMedia(document.Id, 'thumbnail', token, controller.signal)
                .then((blob) => {
                    if (!cancelled && blob) {
                        setThumbnailUrl(URL.createObjectURL(blob));
                    }
                })
                .catch(() => {
                    if (document.ThumbnailFileId) {
                        downloadProviderFile(token, document.ThumbnailFileId)
                            .then((res) => {
                                if (!cancelled && res?.blob) {
                                    setThumbnailUrl(URL.createObjectURL(res.blob));
                                }
                            })
                            .catch(() => {});
                    }
                });
        }

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [document?.Id, document?.ThumbnailFileId, document?.SourceType, document?.MediaFileId, token]);

    React.useEffect(() => {
        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailUrl]);

    const videoTitle = document?.Title || fileBaseName || 'Video Recording';

    return (
        <div className="hc-ai-blob-video-preview">
            <div
                className="hc-ai-blob-video-frame"
                onClick={onOpenModal}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenModal(); }}
                aria-label={`Preview video: ${videoTitle}`}
            >
                {thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={videoTitle}
                        className="hc-ai-blob-video-thumb"
                    />
                ) : (
                    <div className="hc-ai-blob-video-placeholder">
                        <MdMovie size={68} style={{ opacity: 0.28 }} />
                        <span className="mt-2 small opacity-75">Click to Preview Video</span>
                    </div>
                )}

                <div className="hc-ai-blob-video-overlay">
                    <div className="hc-ai-blob-video-badge-top">
                        <span className="hc-ai-blob-video-pill">
                            <MdMovie size={15} color="#a5b4fc" />
                            <span>Video Recording</span>
                        </span>
                        <span className="hc-ai-blob-video-pill" style={{ opacity: 0.95, fontSize: '11.5px' }}>
                            <MdPlayArrow size={15} />
                            <span>Preview in Popup</span>
                        </span>
                    </div>

                    <div className="hc-ai-blob-video-play-center">
                        <div className="hc-ai-blob-video-play-icon">
                            <FaPlay size={20} style={{ marginLeft: 3 }} />
                        </div>
                        <span className="hc-ai-blob-video-play-label">Preview Video</span>
                    </div>

                    <div className="hc-ai-blob-video-bottom-info">
                        <div className="hc-ai-blob-video-title text-truncate" title={videoTitle}>{videoTitle}</div>
                        <div className="hc-ai-blob-video-filename text-truncate">
                            {[fileMeta?.ext ? `${fileMeta.ext} Video` : 'Video', fileSize].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                </div>
            </div>

            <div className="hc-ai-blob-video-meta-bar">
                <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
                    <MdMovie size={20} color="#6366f1" className="flex-shrink-0" />
                    <div className="text-truncate">
                        <div className="fw-semibold text-truncate small">{fileBaseName || 'Attached video file'}</div>
                        <div className="text-muted text-truncate" style={{ fontSize: '11.5px' }}>
                            {[fileMeta?.ext ? `${fileMeta.ext} format` : null, fileSize].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        className="hc-ai-blob-video-action-btn hc-ai-blob-video-btn-play"
                        onClick={onOpenModal}
                    >
                        <MdPlayArrow size={17} />
                        <span>Preview in Popup</span>
                    </button>
                    {onDownload ? (
                        <button
                            type="button"
                            className="hc-ai-blob-video-action-btn hc-ai-blob-video-btn-secondary"
                            onClick={onDownload}
                            title="Download video"
                        >
                            <MdDownload size={16} />
                            <span>Download</span>
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}


export default function AegisAiDocumentsView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );

    // Always start with loading = true so we never render incomplete/guessed data from list rows
    // (which previously caused Jodit editor to flash for a split second before the real preview appeared)
    const [document, setDocument] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [detailsLoading, setDetailsLoading] = React.useState(true);
    const [loadedText, setLoadedText] = React.useState('');
    const [textLoading, setTextLoading] = React.useState(false);
    const [videoPlayerOpen, setVideoPlayerOpen] = React.useState(false);
    const [audioPlayerOpen, setAudioPlayerOpen] = React.useState(false);
    const canEditDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, [PermissionCodes.DOCUMENTS_LIST, PermissionCodes.DOCUMENTS_VIEW], decodedToken);
    const canViewDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_VIEW, decodedToken);

    React.useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                setLoading(true);
                setDetailsLoading(true);
                const response = await getAegisAiDocumentById(id, token);
                if (response?.is_success && response?.data) {
                    const docData = response.data;
                    setDocument(docData);
                    if (docData.ExtractedData) {
                        setLoadedText(docData.ExtractedData);
                    } else if (docData.TextContent) {
                        setLoadedText(docData.TextContent);
                    }
                } else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal server error. Please Contact Support Team.');
                    setDocument(null);
                } else {
                    toast.error(response?.message || 'Failed to load document details');
                    setDocument(null);
                }
            } catch (error) {
                console.error(error);
                setDocument(null);
                toast.error('An error occurred while loading document details');
            } finally {
                setLoading(false);
                setDetailsLoading(false);
            }
        })();
    }, [id, token]);

    React.useEffect(() => {
        if (!document) return;
        const isTxt = Boolean(
            (document.SourceType && ['custom_text', 'text'].includes(String(document.SourceType).toLowerCase())) ||
            (document.FileName && String(document.FileName).toLowerCase().endsWith('.txt')) ||
            document.TextContent
        );
        if (document.ExtractedData) {
            setLoadedText(document.ExtractedData);
            setTextLoading(false);
        } else if (document.TextContent) {
            setLoadedText(document.TextContent);
            setTextLoading(false);
        } else if (isTxt && document.FileId && token) {
            let cancelled = false;
            setTextLoading(true);
            downloadProviderFile(token, document.FileId)
                .then((res) => {
                    if (res?.blob) return res.blob.text();
                    if (res?.data_uri) {
                        const parts = res.data_uri.split(',');
                        if (parts.length > 1) {
                            try { return atob(parts[1]); } catch { return ''; }
                        }
                    }
                    if (res?.data && typeof res.data === 'string') {
                        try { return atob(res.data); } catch { return res.data; }
                    }
                    return '';
                })
                .then((t) => {
                    if (!cancelled) {
                        setLoadedText(t || '');
                        setTextLoading(false);
                    }
                })
                .catch((e) => {
                    console.error('Error fetching text content:', e);
                    if (!cancelled) setTextLoading(false);
                });
            return () => { cancelled = true; };
        } else {
            setTextLoading(false);
        }
    }, [document, token]);

    if (!canViewDocument) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to view this document.</div>
            </div>
        );
    }

    if (loading) {
        // Mirrors the containers below the hero: source on the left, details + pipeline on the right, extracted content full width.
        return (
            <AiViewSkeleton>
                <div className="row g-4">
                    <div className="col-lg-7 d-flex flex-column gap-4">
                        <AiCardSkeleton bodyHeight={360} />
                    </div>
                    <div className="col-lg-5 d-flex flex-column gap-4">
                        <AiCardSkeleton bodyHeight={220} />
                        <AiCardSkeleton bodyHeight={120} />
                    </div>
                    <div className="col-12">
                        <AiCardSkeleton bodyHeight={200} />
                    </div>
                </div>
            </AiViewSkeleton>
        );
    }

    if (!document) {
        return (
            <ViewEmptyState
                title="No Documents Available"
                description="There are no document records available to display."
                icon={MdDescription}
                canViewList={canViewList}
                backRoute={APPRoutes.AEGIS_AI_DOCUMENTS}
                backLabel="Back to Documents"
            />
        );
    }

    const rawStatus = document?.Status || document?.IndexingStatus || 'Pending';
    const statusMeta = getStatusMeta(rawStatus);
    const StatusIcon = statusMeta.icon;
    const createdOn = formatUtcToLocalDateTime(document?.CreatedOn);
    const modifiedOn = formatUtcToLocalDateTime(document?.ModifiedOn);
    const fileMeta = getFileMeta(document?.FileName);
    const FileIcon = fileMeta.Icon;
    const fileBaseName = getBaseName(document?.FileName);
    const sourceType = getAiSourceType(inferAiSourceType({ sourceType: document?.SourceType, url: document?.SourceUrl, fileName: document?.FileName }));
    const SourceIcon = sourceType.Icon;

    const isTextDocument = Boolean(
        (document?.SourceType && ['custom_text', 'text'].includes(document.SourceType.toLowerCase())) ||
        (document?.FileName && document.FileName.toLowerCase().endsWith('.txt'))
    );
    const isVideoDocument = Boolean(
        sourceType.key === 'video' ||
        (!['website', 'youtube', 'custom_text', 'text'].includes(sourceType.key) &&
         fileMeta?.ext && ['mp4', 'webm', 'mov', 'm4v', 'ogv', 'mkv', 'avi'].includes(fileMeta.ext.toLowerCase()))
    );
    const isAudioDocument = Boolean(
        sourceType.key === 'audio' ||
        (!['website', 'youtube', 'custom_text', 'text', 'video'].includes(sourceType.key) &&
         fileMeta?.ext && ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'flac'].includes(fileMeta.ext.toLowerCase()))
    );

    const fileTypeLabel = fileMeta.ext ? `${FILE_TYPE_LABELS[fileMeta.ext] || `${fileMeta.ext} file`} (.${fileMeta.ext.toLowerCase()})` : '-';
    const fileSize = formatFileSize(document?.FileSize);
    const isBinaryDocument = Boolean(
        sourceType.key === 'document' ||
        (fileMeta.ext && ['pdf', 'docx', 'doc'].includes(fileMeta.ext.toLowerCase()))
    );

    let HeroIcon = FileIcon;
    let heroExt = fileMeta.ext;
    if (sourceType.key === 'youtube') {
        HeroIcon = FaYoutube;
        heroExt = null;
    } else if (sourceType.key === 'website') {
        HeroIcon = LuGlobe2;
        heroExt = null;
    } else if (sourceType.key === 'custom_text') {
        HeroIcon = LuFileText;
        heroExt = null;
    } else if (isVideoDocument) {
        HeroIcon = MdMovie;
        heroExt = null;
    } else if (isAudioDocument) {
        HeroIcon = MdHeadphones;
        heroExt = null;
    }
    const sourceUrl = document?.SourceUrl || '';
    const pendingValue = detailsLoading ? <span className="hc-ai-skel overflow-hidden d-inline-block" style={{ width: 90, height: 14 }} /> : '-';
    // File name and type only mean something for uploaded documents; media and links describe themselves in the preview.
    const isDocumentSource = sourceType.key === 'document';
    const hasUploadedFile = Boolean(document?.FileId) && !sourceUrl && sourceType.key !== 'custom_text';
    const detailItems = [
        ...(isDocumentSource && document?.FileId
            ? [
                { key: 'name', icon: MdInsertDriveFile, label: 'File name', value: fileBaseName || '-', wide: true },
                { key: 'type', icon: FileIcon, label: 'File type', value: fileTypeLabel },
            ]
            : [
                { key: 'source', icon: SourceIcon, label: 'Source type', value: sourceUrl ? `${sourceType.label} link` : sourceType.label },
            ]),
        ...(hasUploadedFile ? [{ key: 'size', icon: MdSdStorage, label: 'File size', value: fileSize || pendingValue }] : []),
        ...(sourceUrl ? [{ key: 'url', icon: MdOpenInNew, label: 'Source URL', value: sourceUrl, wide: true }] : []),
        { key: 'client', icon: MdBusiness, label: 'Client', value: document?.ClientName || '-' },
        ...(isDocumentSource ? [{
            key: 'ocr',
            icon: MdDocumentScanner,
            label: 'OCR extraction',
            value: document?.IsOcr ? (
                <span className="badge rounded-pill d-inline-flex align-items-center gap-1 fw-semibold" style={{ backgroundColor: '#e0f2fe', color: '#0284c7', border: '1px solid #bae6fd', fontSize: '11px', padding: '4px 8px' }}>
                    <MdDocumentScanner size={13} />
                    OCR Enabled
                </span>
            ) : (
                <span className="badge rounded-pill d-inline-flex align-items-center gap-1 fw-medium" style={{ backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', fontSize: '11px', padding: '4px 8px' }}>
                    OCR Disabled
                </span>
            ),
        }] : []),
        { key: 'status', icon: StatusIcon, label: 'Indexing status', value: <AiStatusPill tone={statusMeta.tone} label={statusMeta.label} /> },
        { key: 'created', icon: MdEvent, label: 'Created', value: createdOn, sub: document?.CreatedBy ? `by ${document.CreatedBy}` : null },
        { key: 'modified', icon: MdEditCalendar, label: 'Last modified', value: modifiedOn, sub: document?.ModifiedBy ? `by ${document.ModifiedBy}` : null },
    ];
    const pipeline = getPipelineState(statusMeta.tone);
    const CalloutIcon = pipeline.callout.icon;
    // Short source previews (file card, website link) sit beside a much taller details column;
    // moving the pipeline card under them keeps both columns close in height.
    const isCompactSource = !isTextDocument && !(sourceType.key === 'youtube' && sourceUrl) && !isVideoDocument && !isAudioDocument;

    const pipelineCard = (
        <AiCard icon={MdMemory} title="Indexing Pipeline" subtitle="Where this document is in the RAG knowledge pipeline" className="hc-ai-card-fill">
            <div className="hc-ai-pipeline d-grid gap-2 position-relative">
                {PIPELINE_STEPS.map((step, index) => {
                    const state = pipeline.steps[index];
                    const nextState = pipeline.steps[index + 1];
                    const StepIcon = step.icon;
                    const classes = [
                        'hc-ai-pipe-step text-center py-0 px-1',
                        state === 'done' ? 'is-done' : '',
                        state === 'active' ? 'is-active' : '',
                        state === 'muted' ? 'is-muted' : '',
                        nextState === 'active' ? 'is-next-active' : '',
                    ].join(' ');
                    return (
                        <div key={step.key} className={classes}>
                            <div className="hc-ai-pipe-dot d-flex align-items-center justify-content-center">
                                {state === 'muted' ? <MdPauseCircle size={20} /> : <StepIcon size={20} />}
                            </div>
                            <div className="hc-ai-pipe-label fw-bold">{state === 'muted' ? statusMeta.label : step.label}</div>
                            <div className="hc-ai-pipe-caption">{step.caption}</div>
                        </div>
                    );
                })}
            </div>
            <div className={`hc-ai-callout d-flex align-items-start hc-ai-callout-${pipeline.callout.tone}`}>
                <CalloutIcon size={18} />
                <span>{pipeline.callout.text}</span>
            </div>
        </AiCard>
    );

    return (
        <div className="container-fluid py-4 hc-ai-page">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList ? <BackButton onClick={() => navigate(APPRoutes.AEGIS_AI_DOCUMENTS)} /> : <span />}
                {canEditDocument ? (
                    <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary" onClick={() => navigate(APPRoutes.AEGIS_AI_DOCUMENTS_EDIT.replace(':id', document?.Id))}>
                        <MdEdit size={17} />
                        <span>Edit Document</span>
                    </button>
                ) : null}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-teal">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex align-items-start gap-3 gap-md-4">
                            <span className="hc-ai-hero-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white">
                                <HeroIcon size={30} />
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="hc-ai-eyebrow d-inline-flex align-items-center rounded-pill fw-bold text-uppercase text-white">
                                        <MdAutoAwesome size={13} />
                                        Knowledge Document
                                    </span>
                                    <AiStatusPill tone={statusMeta.tone} label={statusMeta.label} icon={StatusIcon} large />
                                    {isDocumentSource ? (
                                        document?.IsOcr ? (
                                            <span
                                                className="badge rounded-pill d-inline-flex align-items-center gap-1 fw-semibold text-white"
                                                style={{
                                                    backgroundColor: 'rgba(2, 132, 199, 0.85)',
                                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    letterSpacing: '0.02em',
                                                    backdropFilter: 'blur(4px)',
                                                }}
                                                title="Extracted with Vision AI OCR enabled"
                                            >
                                                <MdDocumentScanner size={14} />
                                                <span>OCR Enabled</span>
                                            </span>
                                        ) : (
                                            <span
                                                className="badge rounded-pill d-inline-flex align-items-center gap-1 fw-medium text-white-50"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                }}
                                                title="Standard extraction without OCR"
                                            >
                                                <span>OCR Disabled</span>
                                            </span>
                                        )
                                    ) : null}
                                </div>
                                <h2 className="hc-ai-hero-title text-white">{document?.Title || '-'}</h2>
                                <p className="hc-ai-hero-sub m-0">{document?.Description || 'No description provided for this document.'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="hc-ai-glass-panel rounded-4">
                            <div className="hc-ai-glass-label fw-bold text-uppercase d-flex align-items-center gap-1"><MdBusiness size={13} /> Client</div>
                            <div className="hc-ai-glass-value fw-bold text-white">{document?.ClientName || '-'}</div>
                            <div className="hc-ai-glass-divider" />
                            <div className="hc-ai-glass-label fw-bold text-uppercase d-flex align-items-center gap-1"><SourceIcon size={13} /> Knowledge Source Type</div>
                            <div className="hc-ai-glass-value fw-bold text-white d-flex align-items-center gap-2">
                                <span>{sourceType.label}</span>
                                {fileMeta.ext ? (
                                    <span style={{ fontSize: '0.82rem', opacity: 0.85, fontWeight: 500 }}>
                                        ({fileMeta.ext.toUpperCase()})
                                    </span>
                                ) : sourceType.caption ? (
                                    <span style={{ fontSize: '0.82rem', opacity: 0.85, fontWeight: 500 }}>
                                        ({sourceType.caption})
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-7 d-flex flex-column gap-4">
                    {isTextDocument ? (
                        <AiCard
                            icon={LuFileText}
                            title="Document Text Content"
                            subtitle={document?.Title ? `${document.Title} · Text Document` : 'View and copy document content'}
                            className="hc-ai-card-fill"
                            extra={
                                canEditDocument ? (
                                    <button
                                        type="button"
                                        className="hc-ai-btn hc-ai-btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                        onClick={() => navigate(APPRoutes.AEGIS_AI_DOCUMENTS_FORM.replace(':id', document.Id))}
                                    >
                                        <MdEdit size={15} />
                                        <span>Edit Document</span>
                                    </button>
                                ) : null
                            }
                        >
                            {textLoading ? (
                                <div className="d-flex align-items-center justify-content-center p-5 text-muted">
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Loading text editor content...
                                </div>
                            ) : (
                                <AiTextEditor
                                    value={loadedText}
                                    readOnly={true}
                                    fileName={document?.Title || fileBaseName || 'document.txt'}
                                    minHeight={260}
                                    maxHeight={550}
                                />
                            )}
                        </AiCard>
                    ) : (
                        <AiCard
                            icon={
                                sourceType.key === 'youtube' && sourceUrl ? FaYoutube :
                                sourceType.key === 'website' || (sourceUrl && !isVideoDocument && !isAudioDocument) ? LuGlobe2 :
                                isVideoDocument ? MdMovie :
                                isAudioDocument ? MdHeadphones :
                                SourceIcon || MdFolder
                            }
                            title={
                                sourceType.key === 'youtube' && sourceUrl ? 'YouTube Video' :
                                sourceType.key === 'website' || (sourceUrl && !isVideoDocument && !isAudioDocument) ? 'Website Link' :
                                isVideoDocument ? 'Video Recording' :
                                isAudioDocument ? 'Audio Recording' :
                                'Source File'
                            }
                            subtitle={
                                sourceType.key === 'youtube' && sourceUrl ? "The YouTube video indexed for this client's assistant" :
                                sourceType.key === 'website' || (sourceUrl && !isVideoDocument && !isAudioDocument) ? "The website link indexed for this client's assistant" :
                                isVideoDocument ? "The video recording indexed for this client's assistant" :
                                isAudioDocument ? "The audio recording indexed for this client's assistant" :
                                "The file indexed for this client's assistant"
                            }
                            className={isCompactSource ? 'h-auto' : 'hc-ai-card-fill'}
                        >
                            {sourceType.key === 'youtube' && sourceUrl ? (
                                <AiYouTubePreview url={sourceUrl} title={document?.Title} token={token} />
                            ) : sourceType.key === 'website' || (sourceUrl && !isVideoDocument && !isAudioDocument) ? (
                                <div className="hc-ai-file d-flex align-items-center p-3 rounded-4">
                                    <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': '#0ea5e9' }}>
                                        <LuGlobe2 size={24} />
                                    </span>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="hc-ai-file-name fw-bold text-truncate">Website link</div>
                                        <div className="hc-ai-file-meta text-truncate" title={sourceUrl}>{sourceUrl}</div>
                                    </div>
                                    <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline flex-shrink-0">
                                        <MdOpenInNew size={16} />
                                        <span>Open</span>
                                    </a>
                                </div>
                            ) : isVideoDocument ? (
                                <>
                                    <AiBlobVideoPreview
                                        document={document}
                                        fileMeta={fileMeta}
                                        fileSize={fileSize}
                                        fileBaseName={fileBaseName}
                                        token={token}
                                        posterUrl={document?.ThumbnailUrl}
                                        onOpenModal={() => setVideoPlayerOpen(true)}
                                        onDownload={document?.FileId ? () => downloadProviderFile(token, document.FileId).then(res => {
                                            if (res?.blob) {
                                                const u = URL.createObjectURL(res.blob);
                                                const a = window.document.createElement('a');
                                                a.href = u;
                                                a.download = res.filename || fileBaseName || 'video.mp4';
                                                window.document.body.appendChild(a);
                                                a.click();
                                                window.document.body.removeChild(a);
                                                setTimeout(() => URL.revokeObjectURL(u), 2000);
                                            }
                                        }) : null}
                                    />
                                    <AiVideoPlayerModal
                                        isOpen={videoPlayerOpen}
                                        onClose={() => setVideoPlayerOpen(false)}
                                        title={document?.Title}
                                        fileName={fileBaseName}
                                        fileSize={fileSize}
                                        videoUrl={document?.MediaStreamUrl}
                                        posterUrl={document?.ThumbnailUrl}
                                        documentId={document?.Id}
                                        fileId={document?.FileId}
                                    />
                                </>
                            ) : isAudioDocument ? (
                                <>
                                    <AiBlobAudioPreview
                                        document={document}
                                        fileMeta={fileMeta}
                                        fileSize={fileSize}
                                        fileBaseName={fileBaseName}
                                        token={token}
                                        onOpenModal={() => setAudioPlayerOpen(true)}
                                        onDownload={document?.FileId ? () => downloadProviderFile(token, document.FileId).then(res => {
                                            if (res?.blob) {
                                                const u = URL.createObjectURL(res.blob);
                                                const a = window.document.createElement('a');
                                                a.href = u;
                                                a.download = res.filename || fileBaseName || 'audio.mp3';
                                                window.document.body.appendChild(a);
                                                a.click();
                                                window.document.body.removeChild(a);
                                                setTimeout(() => URL.revokeObjectURL(u), 2000);
                                            }
                                        }) : null}
                                    />
                                    <AiAudioPlayerModal
                                        isOpen={audioPlayerOpen}
                                        onClose={() => setAudioPlayerOpen(false)}
                                        title={document?.Title}
                                        fileName={fileBaseName}
                                        fileSize={fileSize}
                                        audioUrl={document?.MediaStreamUrl}
                                        documentId={document?.Id}
                                        fileId={document?.FileId}
                                        onDownload={document?.FileId ? () => downloadProviderFile(token, document.FileId).then(res => {
                                            if (res?.blob) {
                                                const u = URL.createObjectURL(res.blob);
                                                const a = window.document.createElement('a');
                                                a.href = u;
                                                a.download = res.filename || fileBaseName || 'audio.mp3';
                                                window.document.body.appendChild(a);
                                                a.click();
                                                window.document.body.removeChild(a);
                                                setTimeout(() => URL.revokeObjectURL(u), 2000);
                                            }
                                        }) : null}
                                    />
                                </>
                            ) : document?.FileId ? (
                                <>
                                    <div className="hc-ai-file d-flex align-items-center justify-content-between p-3 rounded-4 gap-3">
                                        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                            <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': fileMeta.color }}>
                                                <FileIcon size={24} />
                                            </span>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="hc-ai-file-name fw-bold text-truncate">{fileBaseName || 'Attached file'}</div>
                                                <div className="hc-ai-file-meta">
                                                    {[fileMeta.ext ? `${fileMeta.ext} ${sourceType.key === 'document' ? 'document' : 'file'}` : 'Document', fileSize].filter(Boolean).join(' · ')}
                                                </div>
                                            </div>
                                        </div>
                                        {isDocumentSource ? (
                                            <div className="flex-shrink-0">
                                                {document?.IsOcr ? (
                                                    <div
                                                        className="hc-ai-ocr-interactive-badge d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold shadow-sm"
                                                        style={{
                                                            background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.12) 0%, rgba(14, 165, 233, 0.22) 100%)',
                                                            color: '#0284c7',
                                                            border: '1.5px solid #38bdf8',
                                                        }}
                                                        title="OCR Enabled Document: Text recognized using Vision AI OCR for scanned pages and images"
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center justify-content-center rounded-circle text-white flex-shrink-0"
                                                            style={{
                                                                width: 22,
                                                                height: 22,
                                                                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                                                boxShadow: '0 2px 6px rgba(2, 132, 199, 0.35)',
                                                            }}
                                                        >
                                                            <MdDocumentScanner size={13} />
                                                        </span>
                                                        <span style={{ fontSize: '0.82rem', letterSpacing: '0.01em' }}>OCR Enabled</span>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="hc-ai-ocr-interactive-badge is-disabled d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-medium"
                                                        style={{
                                                            background: '#f8fafc',
                                                            color: '#64748b',
                                                            border: '1.5px solid #cbd5e1',
                                                        }}
                                                        title="OCR Disabled Document: Extracted directly from digital embedded text without OCR"
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                                            style={{ width: 22, height: 22, background: '#e2e8f0', color: '#64748b' }}
                                                        >
                                                            <MdDocumentScanner size={13} />
                                                        </span>
                                                        <span style={{ fontSize: '0.82rem' }}>OCR Disabled</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                    <FileActions fileId={document.FileId} fileName={getBaseName(document.FileName)} onPreview={previewAnyFile} skipReAuth={true} />
                                </>
                            ) : (
                                <div className="hc-ai-empty-inline d-flex align-items-center">
                                    <MdInfoOutline size={20} className="flex-shrink-0" />
                                    <span>No file attached to this document.</span>
                                </div>
                            )}
                        </AiCard>
                    )}
                    {isCompactSource ? pipelineCard : null}
                </div>

                <div className="col-lg-5 d-flex flex-column gap-4">
                    <AiCard icon={MdInsertDriveFile} title="Document Details" subtitle="Source, indexing and record information for this document" className={isCompactSource ? 'hc-ai-card-fill' : 'h-auto'}>
                        <dl className="hc-ai-details d-grid m-0">
                            {detailItems.map(({ key, icon: ItemIcon, label, value, sub, wide, mono }) => (
                                <div key={key} className={`hc-ai-detail d-flex align-items-start ${wide ? 'is-wide' : ''}`}>
                                    <span className="hc-ai-detail-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                        <ItemIcon size={16} />
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <dt>{label}</dt>
                                        <dd className={mono ? 'is-mono' : ''}>{value}</dd>
                                        {sub ? <div className="hc-ai-detail-sub">{sub}</div> : null}
                                    </div>
                                </div>
                            ))}
                        </dl>
                    </AiCard>
                    {isCompactSource ? null : pipelineCard}
                </div>

                {!isTextDocument && !isBinaryDocument && loadedText ? (
                    <div className="col-12">
                        <AiCard
                            icon={MdFactCheck}
                            title="Extracted Knowledge Content"
                            subtitle="Latest reviewed content indexed from this source in the knowledge base"
                            className="h-auto"
                            extra={
                                canEditDocument ? (
                                    <button
                                        type="button"
                                        className="hc-ai-btn hc-ai-btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                        onClick={() => navigate(APPRoutes.AEGIS_AI_DOCUMENTS_EDIT.replace(':id', document.Id))}
                                    >
                                        <MdEdit size={15} />
                                        <span>Edit Content</span>
                                    </button>
                                ) : null
                            }
                        >
                            <AiTextEditor
                                value={loadedText}
                                readOnly={true}
                                fileName={document?.Title || fileBaseName || 'extracted_content.txt'}
                                minHeight={200}
                                maxHeight={400}
                            />
                        </AiCard>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
